//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { ITextInputValidation } from '@components/text-input/textInputModel';

const MIN_GTIN_LENGTH = 12;
const MAX_GTIN_LENGTH = 14;
const PZN7_LENGTH = 7;
const PZN8_LENGTH = 8;

export const ErrorMessages = {
  invalidLength: `Identifier must be 7, 8, 12, 13, or 14 digits long.`,
  invalidChecksum:
    'The identifier appears to be incorrect. Please double-check the number and its checksum.',
  mustNotBeSequential:
    'Identifiers should not contain sequential digits (e.g., 1234)',
  mustNotBeRepeating:
    'Identifiers should not contain repeating digits (e.g., 1111)',
  emptyCodeError: 'Identifier cannot be empty',
} as const;

/**
 * Check if GTIN consists of all repeating digits (e.g., 000000, 111111)
 */
const isRepeatingDigits = (gtin: string): boolean => {
  return /^(\d)\1+$/.test(gtin);
};

/**
 * Check if GTIN is a simple sequence like '123456789...' or '012345...'
 */
const isSequentialDigits = (gtin: string): boolean => {
  // Check common sequential patterns
  if (gtin === '123456789012') {
    return true;
  }

  // Check if it's a substring of the sequential pattern 0123456789
  const sequence = '0123456789012345';
  if (sequence.includes(gtin)) {
    return true;
  }

  return false;
};

/**
 * The following GTIN validation functions are adapted from:
 * https://github.com/xbpf/gtin
 */
const to13Digits = (barcode: string): string => {
  return barcode.padStart(13, '0');
};

const generateCheckDigit = (barcode: string): string => {
  return String(
    (10 -
      (to13Digits(barcode)
        .split('')
        .map((num, idx) => +num * (idx % 2 === 0 ? 3 : 1))
        .reduce((prev, cur) => prev + cur) %
        10)) %
      10,
  );
};

const isGTIN = (barcode: string): boolean => {
  if (barcode === '') {
    throw new Error(ErrorMessages.emptyCodeError);
  }

  return /^(\d{12,14}|\d{8})$/.test(barcode);
};

const isValidGTIN = (barcode: string): boolean => {
  if (!isGTIN(barcode)) {
    return false;
  }

  const checkDigit = barcode.slice(-1);
  const generatedCheckDigit = generateCheckDigit(barcode.slice(0, -1));

  return checkDigit === generatedCheckDigit;
};

/**
 * Validates a German Pharmacy Product Number (PZN).
 * The validation is based on a modulo-11 checksum algorithm.
 *
 * @param pzn The PZN string to validate. It must be a string of 7 or 8 digits.
 * @returns `true` if the PZN is valid, otherwise `false`.
 */
const isValidPZN = (pzn: string): boolean => {
  const len = pzn.length;

  if (len !== PZN7_LENGTH && len !== PZN8_LENGTH) {
    return false;
  }

  const data = pzn.slice(0, -1);
  const checkDigit = pzn.slice(-1);

  const sum = data
    .split('')
    .reduce((acc, digit, i) => acc + parseInt(digit, 10) * (i + 1), 0);

  const calculatedCheckDigit = sum % 11;

  if (calculatedCheckDigit === 10) {
    return false;
  }

  return calculatedCheckDigit === parseInt(checkDigit, 10);
};

/**
 * Validates a product identifier (GTIN or PZN)
 * @param identifier The identifier to validate
 * @returns ITextInputValidation with isValid flag and optional error message
 */
export const validateProductIdentifier = (
  identifier: string,
): ITextInputValidation => {
  const len = identifier.length;

  if (
    len !== PZN7_LENGTH &&
    len !== PZN8_LENGTH &&
    (len < MIN_GTIN_LENGTH || len > MAX_GTIN_LENGTH)
  ) {
    return { isValid: false, reason: ErrorMessages.invalidLength };
  }

  if (isRepeatingDigits(identifier)) {
    return { isValid: false, reason: ErrorMessages.mustNotBeRepeating };
  }

  if (isSequentialDigits(identifier)) {
    return { isValid: false, reason: ErrorMessages.mustNotBeSequential };
  }

  const isValid =
    len === PZN7_LENGTH || len === PZN8_LENGTH
      ? isValidPZN(identifier)
      : isValidGTIN(identifier);

  if (isValid) {
    return { isValid: true };
  }

  return {
    isValid,
    ...(!isValid && { reason: ErrorMessages.invalidChecksum }),
  };
};
